import { useRef, useState, type CSSProperties, type ReactNode } from "react";
import type { Entity } from "@kkindex/shared";
import { Draggable, Drop } from "./components";
import { useSelection } from "./interaction";
import type { Execute } from "./Editor";
import s from "./App.module.css";

export function TaskCard({
  item,
  column,
  columnCount,
  closed,
  draggingParent,
  mobile,
  header,
  children,
  execute,
  edit,
}: {
  item: Entity;
  column: number;
  columnCount: number;
  closed: boolean;
  draggingParent: boolean;
  mobile: boolean;
  header: ReactNode;
  children: ReactNode;
  execute: Execute;
  edit: (item: Entity) => void;
}) {
  const { selected } = useSelection();
  const selectionKey = `parent:${item.id}`;
  const active = selected === selectionKey;
  const ref = useRef<HTMLDivElement>(null);
  const gesture = useRef<{
    y: number;
    height: number;
    revision: number;
  } | null>(null);
  const [height, setHeight] = useState<number | null | undefined>();
  const [saving, setSaving] = useState(false);
  const [resizing, setResizing] = useState(false);
  const shownHeight = height === undefined ? item.cardHeight : height;
  const clamp = (value: number) =>
    Math.max(160, Math.min(1000, Math.round(value)));
  const save = async (value: number | null, revision = item.revision) => {
    if (value === item.cardHeight) {
      setHeight(undefined);
      return;
    }
    setHeight(value);
    setSaving(true);
    try {
      await execute(`/items/${item.id}`, "PATCH", {
        cardHeight: value,
        revision,
      });
    } catch {
      /* The shared mutation handler retains retry content. */
    } finally {
      setHeight(undefined);
      setSaving(false);
    }
  };
  const cancel = () => {
    gesture.current = null;
    setHeight(undefined);
    setResizing(false);
  };
  return (
    <div
      ref={ref}
      data-card-id={item.id}
      data-card-column={column}
      className={`${s.cardShell} ${closed ? s.cardClosed : ""} ${resizing || active ? s.cardResizing : ""}`}
      style={
        {
          "--item-color": item.color,
          height: !closed && !mobile && shownHeight ? shownHeight : undefined,
          maxHeight: !mobile && !shownHeight ? 480 : undefined,
        } as CSSProperties
      }
    >
      <Draggable
        item={item}
        id={`parent:${item.id}`}
        selectionKey={selectionKey}
        onOpen={() => edit(item)}
        disabled={mobile}
        data={{ surface: "parent" }}
        className={s.taskGroup}
      >
        <Drop
          id={`parent-drop:${item.id}`}
          data={{ surface: "parent", itemId: item.id }}
          accept={item.archived ? [] : ["task"]}
          className={s.taskGroup}
        >
          <div className={s.cardHeader}>{header}</div>
          <div className={s.cardBody}>{children}</div>
          {!closed && !item.archived && !mobile && active && (
            <button
              className={s.cardResize}
              disabled={saving}
              aria-label={`调整卡片高度：${item.title}`}
              title="拖动调整高度；双击恢复自动高度；方向键微调，Home 恢复自动"
              onDoubleClick={() => void save(null)}
              onKeyDown={(event) => {
                if (event.key === "Escape") {
                  event.preventDefault();
                  cancel();
                }
                if (event.key === "Home") {
                  event.preventDefault();
                  void save(null);
                }
                if (event.key === "ArrowUp" || event.key === "ArrowDown") {
                  event.preventDefault();
                  const delta =
                    (event.shiftKey ? 100 : 20) *
                    (event.key === "ArrowDown" ? 1 : -1);
                  void save(
                    clamp(ref.current!.getBoundingClientRect().height + delta),
                  );
                }
              }}
              onPointerDown={(event) => {
                if (event.button !== 0 || !active) return;
                event.preventDefault();
                event.stopPropagation();
                event.currentTarget.focus();
                event.currentTarget.setPointerCapture(event.pointerId);
                gesture.current = {
                  y: event.clientY,
                  height: ref.current!.getBoundingClientRect().height,
                  revision: item.revision,
                };
                setResizing(true);
              }}
              onPointerMove={(event) => {
                if (
                  gesture.current &&
                  Math.abs(event.clientY - gesture.current.y) >= 8
                )
                  setHeight(
                    clamp(
                      gesture.current.height +
                        event.clientY -
                        gesture.current.y,
                    ),
                  );
              }}
              onPointerUp={(event) => {
                const start = gesture.current;
                gesture.current = null;
                setResizing(false);
                if (event.currentTarget.hasPointerCapture(event.pointerId))
                  event.currentTarget.releasePointerCapture(event.pointerId);
                if (start && Math.abs(event.clientY - start.y) >= 8)
                  void save(
                    clamp(start.height + event.clientY - start.y),
                    start.revision,
                  );
                else setHeight(undefined);
              }}
              onPointerCancel={cancel}
              onLostPointerCapture={() => {
                if (gesture.current) cancel();
              }}
            >
              <span />
            </button>
          )}
        </Drop>
      </Draggable>
      {draggingParent &&
        !item.archived &&
        [false, true].map((after) => (
          <Drop
            key={String(after)}
            id={`card-${after ? "after" : "before"}:${item.id}`}
            accept={["parent"]}
            label={`${after ? "放在后面" : "放在前面"}：${item.title}`}
            data={{
              surface: "card-layout",
              itemId: item.id,
              column,
              columnCount,
              after,
            }}
            className={`${s.cardDropZone} ${after ? s.cardDropAfter : s.cardDropBefore}`}
          >
            <span>{after ? "放在此卡片下方" : "放在此卡片上方"}</span>
          </Drop>
        ))}
    </div>
  );
}
